import { formatSalesOrderNumber } from '@order-management/format';
import {
  ALL_ZSPACE_STAFF_AND_PARTNER_PERMISSIONS_GROUPS,
  PermissionsGroupName,
  UserPermissions,
  getUserHighestPermissionsGroupUserRoleAssignment,
} from '@order-management/roles';
import { SalesOrder, User, UserRoleType } from '@order-management/types';
import { useCallback, useMemo, useState } from 'react';
import { Element, Icon } from 'react-bulma-components';
import { FaPenToSquare } from 'react-icons/fa6';
import CheckPermissions from '../../shared/check-permissions/check-permissions';
import Conditional from '../../shared/conditional/conditional';
import { remainingDays } from '../../shared/dates';
import useIsUserEditable from '../../shared/hooks/is-user-editable';
import useUserAllowedPermissionsGroupsToManage from '../../shared/hooks/user-allowed-permissions-groups-to-manage';
import If from '../../shared/if/if';
import { formatLastActiveDate } from '../../shared/utils';
import Button from '../../ui/button/button';
import PaginatedTable, {
  CustomPaginatedTableProps,
} from '../../ui/table/paginated-table/paginated-table';
import {
  Column,
  OnTableChangeFunction,
  RowInteraction,
} from '../../ui/table/types';
import DisableUserConfirmationModal from '../disable-user-confirmation-modal/disable-user-confirmation-modal';
import EditUserPermissionsModal from '../edit-user-permissions-modal/edit-user-permissions-modal';
import { UserStatusText } from '../types';
import styles from './users-table.module.scss';

export type UsersTableProps = CustomPaginatedTableProps<User> & {
  onChange: OnTableChangeFunction<User, PermissionsGroupBySalesOrder>;
  handleDisableUser?: (id: string) => Promise<void>;
  handleEditUserPermissions?: (
    id: string,
    newPermissionsGroup?: PermissionsGroupName
  ) => Promise<void>;
  handleUserTemporaryPermissionsClick?: (user: User) => void;
  loading?: boolean;
};

export type PermissionsGroupBySalesOrder = {
  permissionsGroupName: PermissionsGroupName;
  salesOrders: SalesOrder[];
};

export function UsersTable({
  dataSource,
  onChange,
  config,
  empty,
  form,
  handleDisableUser,
  handleEditUserPermissions,
  loading,
}: UsersTableProps) {
  const currentUserAllowedPermissionsGroupsToManage =
    useUserAllowedPermissionsGroupsToManage();

  const [activeRowId, setActiveRowId] = useState<string>('');
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [showDisableConfirmationModal, setShowDisableConfirmationModal] =
    useState(false);
  const [showEditPermissionsModal, setShowEditPermissionsModal] =
    useState(false);

  const rowInteraction: RowInteraction = {
    onRowEnter: function (rowKey: string): void {
      setActiveRowId(rowKey);
    },
    onRowLeave: function (): void {
      setActiveRowId('');
    },
  };

  const onDisableUser = useCallback(
    async (id: string) => {
      await handleDisableUser?.(id);
      setSelectedUser(undefined);
      setShowDisableConfirmationModal(false);
    },
    [handleDisableUser]
  );

  const { isOwnUser } = useIsUserEditable();

  const onEditUserPermissions = useCallback(
    async (id: string, newPermissionsGroup?: PermissionsGroupName) => {
      await handleEditUserPermissions?.(id, newPermissionsGroup);
      setSelectedUser(undefined);
      setShowEditPermissionsModal(false);
    },
    [handleEditUserPermissions]
  );

  const isDisabledRow = useCallback((row: User) => row.deletedAt !== null, []);

  const columns: Column<User>[] = useMemo(
    () => [
      {
        key: 'email',
        dataIndex: 'email',
        title: 'User',
        className: 'is-width-25',
        sortable: true,
      },
      {
        key: 'roles.role.name',
        className: 'is-width-20',
        render: (el) => {
          const isWarehousePartnerUser = el.roles.every(
            (role) => role.role.name === UserRoleType.PARTNER
          );
          const userPermissionsGroupUserRoleAssignment = isWarehousePartnerUser
            ? el.roles.find((role) => role.role.name === UserRoleType.PARTNER)
                ?.permissionsGroupUserRoleAssignments[0]
            : getUserHighestPermissionsGroupUserRoleAssignment(
                el,
                UserRoleType.ZSPACE_INTERNAL
              );
          const role =
            userPermissionsGroupUserRoleAssignment?.permissionsGroup?.name ??
            'Staff';
          const hasEditableRole =
            !isWarehousePartnerUser &&
            currentUserAllowedPermissionsGroupsToManage.length > 0 &&
            (role === 'Staff' ||
              currentUserAllowedPermissionsGroupsToManage.includes(
                role as PermissionsGroupName
              )) &&
            !isOwnUser(el) &&
            !el.deletedAt;

          return (
            <Element display="flex" alignItems="center">
              <span>{role}</span>
              <CheckPermissions
                permissions={UserPermissions.USERS_ASSIGN_ROLES}
              >
                <CheckPermissions.Render>
                  <If condition={hasEditableRole}>
                    <Button
                      ml={1}
                      text
                      className={`has-text-primary-dark is-size-7 ${
                        el.id === activeRowId ? '' : `${styles.invisible}`
                      }`}
                      onClick={() => {
                        setSelectedUser(el);
                        setShowEditPermissionsModal(true);
                      }}
                    >
                      <Icon>
                        <FaPenToSquare />
                      </Icon>
                      <span>Edit</span>
                    </Button>
                  </If>
                </CheckPermissions.Render>
              </CheckPermissions>
            </Element>
          );
        },
        title: 'Role',
      },
      {
        key: 'temporaryRoles',
        render: (el) => {
          const temporaryPermissionsGroupUserRoleAssignments = el.roles
            .find(
              (roleAssignment) =>
                roleAssignment.role.name === UserRoleType.CUSTOMER
            )
            ?.permissionsGroupUserRoleAssignments.filter(
              (roleAssignment) =>
                roleAssignment.permissionsGroup.name ===
                PermissionsGroupName.TEMPORARY_SALES_ORDER_MANAGER
            );

          if (
            !temporaryPermissionsGroupUserRoleAssignments ||
            temporaryPermissionsGroupUserRoleAssignments.length === 0
          ) {
            return '';
          }

          return temporaryPermissionsGroupUserRoleAssignments.map(
            (permissionsGroupUserRoleAssignment) => {
              const daysLeft = remainingDays(
                new Date(permissionsGroupUserRoleAssignment.expiresAt!)
              );

              if (!daysLeft || !permissionsGroupUserRoleAssignment.salesOrder) {
                return null;
              }

              const content = `${
                permissionsGroupUserRoleAssignment.salesOrder.opportunity
                  ?.account?.name ??
                formatSalesOrderNumber(
                  permissionsGroupUserRoleAssignment.salesOrder.number
                )
              } (${daysLeft})`;

              return (
                <Element>
                  <span className="is-size-7 is-underlined">{content}</span>
                </Element>
              );
            }
          );
        },
        title: 'Temporary manager roles (days remaining)',
        sortable: false,
      },
      {
        key: 'lastActive',
        className: 'is-width-10',
        render: (el) =>
          el.lastActive ? formatLastActiveDate(new Date(el.lastActive)) : '',
        title: 'Last active',
        sortable: true,
      },
      {
        key: 'deletedAt',
        className: 'is-width-15',
        render: (el) => (
          <Element display="flex" alignItems="center">
            <Conditional condition={el.deletedAt === null}>
              <Conditional.True>
                <span>
                  {el.lastActive
                    ? UserStatusText.ACTIVE
                    : UserStatusText.PENDING_FIRST_LOGIN}
                </span>
                <CheckPermissions
                  permissions={UserPermissions.USERS_DELETE}
                  allowedPermissionGroups={
                    ALL_ZSPACE_STAFF_AND_PARTNER_PERMISSIONS_GROUPS
                  }
                >
                  <CheckPermissions.Render>
                    <If condition={!isOwnUser(el)}>
                      <Button
                        ml={1}
                        text
                        className={`has-text-danger-dark is-size-7 ${
                          el.id === activeRowId ? '' : `${styles.invisible}`
                        }`}
                        onClick={() => {
                          setSelectedUser(el);
                          setShowDisableConfirmationModal(true);
                        }}
                      >
                        <span className="is-underlined">Disable</span>
                      </Button>
                    </If>
                  </CheckPermissions.Render>
                </CheckPermissions>
              </Conditional.True>
              <Conditional.False>
                <span>Inactive</span>
              </Conditional.False>
            </Conditional>
          </Element>
        ),
        title: 'Status',
        sortable: false,
      },
    ],
    [activeRowId, isOwnUser, currentUserAllowedPermissionsGroupsToManage]
  );

  return (
    <>
      <PaginatedTable
        dataSource={dataSource}
        columns={columns}
        onChange={onChange}
        config={config}
        empty={empty}
        rowKey={'id'}
        form={form}
        rowInteraction={rowInteraction}
        isDisabledSelection={isDisabledRow}
      />
      <If condition={!!selectedUser}>
        <DisableUserConfirmationModal
          show={showDisableConfirmationModal}
          user={selectedUser!}
          loading={!!loading}
          onCancel={() => {
            setShowDisableConfirmationModal(false);
            setSelectedUser(undefined);
          }}
          onDisable={onDisableUser}
        />
        <EditUserPermissionsModal
          show={showEditPermissionsModal}
          user={selectedUser!}
          onCancel={() => {
            setShowEditPermissionsModal(false);
            setSelectedUser(undefined);
          }}
          onEditUserPermissions={onEditUserPermissions}
        />
      </If>
    </>
  );
}

export default UsersTable;
